import React, { useState } from "react";
import { Link } from "react-router-dom";
import DOMPurify from "dompurify";
import { usePosts, Post } from "../hooks/usePosts";

import SEO from "../components/SEO";
import { seoConfig } from "../seoConfig";

import GetInTouch from '../components/GetInTouch';

const Blog: React.FC = () => {
  const seo = seoConfig.blog;

  const [page, setPage] = useState(1);
  const { data, isLoading, isError } = usePosts(page, 2);

  const posts: Post[] = data?.posts ?? [];
  const totalPages = data?.totalPages ?? 1;

  // Helper function to strip HTML tags from string
  function stripHtml(html: string) {
    const div = document.createElement("div");
    div.innerHTML = html;
    return div.textContent || div.innerText || "";
  }

  return (
    
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        keywords={seo.keywords}
        url={seo.url}
        image={seo.image}
      />

      {/* Breadcrumb area here */}
      <div className="rts-breadcrumb-area-blog bg_image">
        <div className="container">
          <div className="row">
            <div className="col-lg-12">
              <div className="baread-crumb-main-wrapper pt--100">
                <div className="inner">
                  <h1 className="title rts_hero__title">Blog</h1>
                  <span className="bg-text d-none d-md-inline">Manikarn Digital</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container pb--120">
        {isLoading && <p>Loading posts...</p>}
        {isError && <p>Failed to load posts.</p>}
        {!isLoading && posts.length === 0 && <p>No posts found.</p>}

        <div className="row">
          <div className="col-xl-8 col-lg-12 col-md-12">
            <div className="row">
              {posts.map((p) => {
                const img = p._embedded?.["wp:featuredmedia"]?.[0]?.source_url;

                // Sanitize and strip HTML tags from excerpt, then truncate to 50 words
                const sanitizedExcerpt = DOMPurify.sanitize(p.excerpt.rendered);
                const textExcerpt = stripHtml(sanitizedExcerpt);
                const words = textExcerpt.split(/\s+/).slice(0, 25).join(" ") + "...";

                return (
                  <div key={p.id} className="col-md-6">
                    <div className="single-blog-area-5 with-sidebar mt--0 mb--30">
                      <Link to={`/blog/${p.slug}`} className="thumbnail">
                        {img ? (
                          <img
                            src={img}
                            alt={p.title.rendered}
                            style={{
                              height: "250px",
                              width: "100%",
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                          />
                        ) : (
                          <img
                            src="assets/images/blog/placeholder.jpg"
                            alt="placeholder"
                            style={{
                              height: "250px",
                              width: "100%",
                              objectFit: "cover",
                              objectPosition: "center",
                            }}
                          />
                        )}
                      </Link>
                      <div className="inner-content">
                        <Link to={`/blog/${p.slug}`}>
                          <h3
                            className="title animated fadeIn"
                            dangerouslySetInnerHTML={{ __html: p.title.rendered }}
                          />
                        </Link>
                        <p className="disc">{words}</p>
                        <Link to={`/blog/${p.slug}`} className="read-more-bb mt--20">
                          Read More
                        </Link>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Pagination */}
            <div className="rts-mighty-pagination">
              <ul>
                {[...Array(totalPages).keys()].map((num) => (
                  <li key={num}>
                    <button
                      className={page === num + 1 ? "active" : ""}
                      onClick={() => setPage(num + 1)}
                    >
                      {(num + 1).toString().padStart(2, "0")}
                    </button>
                  </li>
                ))}
                <li>
                  <button
                    onClick={() => setPage((s) => Math.min(totalPages, s + 1))}
                    disabled={page >= totalPages}
                  >
                    <i className="fa-solid fa-angles-right" />
                  </button>
                </li>
              </ul>
            </div>
          </div>

          <div className="col-xl-4 col-lg-12 col-md-12 col-sm-12 col-12 pl--30 pl_md--0 pl_sm--0 pt_md--60 pt_sm--60 h-100 rts-sticky-column-item">
            {/* single widget start */}
            <div className="rts-single-wized Categories">
              <div className="wized-header">
                <h5 className="title">Our Services</h5>
              </div>
              <div className="wized-body">
                {/* categories */}
                <ul className="single-categories">
                  <li>
                    <Link to="/web-design-development">
                      Web Design & Development <i className="fa-solid fa-arrow-right-long" />
                    </Link>
                  </li>
                </ul>
                <ul className="single-categories">
                  <li>
                    <Link to="/digital-marketing">
                      Digital Marketing
                      <i className="fa-solid fa-arrow-right-long" />
                    </Link>
                  </li>
                </ul>
                <ul className="single-categories">
                  <li>
                    <Link to="/graphic-designing">
                      Graphic Designing
                      <i className="fa-solid fa-arrow-right-long" />
                    </Link>
                  </li>
                </ul>
                <ul className="single-categories">
                  <li>
                    <Link to="/hosting-support">
                      Hosting and Support
                      <i className="fa-solid fa-arrow-right-long" />
                    </Link>
                  </li>
                </ul>
                <ul className="single-categories">
                  <li>
                    <Link to="/website-care">
                      Website Care
                      <i className="fa-solid fa-arrow-right-long" />
                    </Link>
                  </li>
                </ul>
              </div>
            </div>
            {/* single widget end */}
          </div>
        </div>
      </div>

      {/* ==== GetInTouch Section start ==== */}
      <GetInTouch />
      {/* ==== / GetInTouch Section end ==== */}
    </>
  );
};

export default Blog;
